<template>
    <div class="container mt-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0 text-vertex-blue"><strong>Contract Section Sequence</strong></h5>
            <button class="btn btn-sm btn-outline-info" style="border-radius: 8px!important;"
                @click="saveSequence" :disabled="isSaving || !draggableSections.length">
                <i class="fa-solid fa-save me-1"></i> {{ isSaving ? 'Saving...' : 'Save Sequence' }}
            </button>
        </div>
        <p class="text-muted small mb-4">Drag and drop the sections below to reorder your contract.
            Click the eye icon to hide or show a section from the client.</p>

        <!-- Draggable sections (everything except Schedule & Execution) -->
        <draggable v-model="draggableSections" group="sections" item-key="type"
            @end="onReorderEnd" animation="200">
            <template #item="{ element, index }">
                <div class="draggable-row d-flex align-items-center mb-2 p-2 border rounded bg-white shadow-sm transition-all"
                    :class="{ 'opacity-50': element.is_hidden }">

                    <i class="fa-solid fa-grip-vertical me-3 text-muted fs-5" title="Drag to reorder"></i>

                    <span class="flex-grow-1 fw-bold"
                        :class="{ 'text-decoration-line-through text-muted': element.is_hidden }"
                        style="font-size: 15px;">
                        {{ formatSectionName(element.type) }}
                    </span>

                    <i class="fa-solid fs-5"
                        :class="element.is_hidden ? 'fa-eye-slash text-danger' : 'fa-eye text-success'"
                        style="cursor: pointer;"
                        :title="element.is_hidden ? 'Section is hidden' : 'Section is visible'"
                        @click.stop="toggleDraggableVisibility(index)">
                    </i>
                </div>
            </template>
        </draggable>

        <!-- Locked sections (Schedule & Execution) — always pinned at the bottom, always visible -->
        <div v-for="element in lockedSections" :key="element.type"
            class="d-flex align-items-center mb-2 p-2 border rounded shadow-sm transition-all bg-light"
            style="border-left: 3px solid #00A0C1 !important;">

            <i class="fa-solid fa-lock me-3 text-muted fs-6" title="This section is locked in position"></i>

            <span class="flex-grow-1 fw-bold" style="font-size: 15px;">
                {{ formatSectionName(element.type) }}
            </span>

            <span class="badge bg-secondary me-2" style="font-size: 10px;">Locked</span>
            <i class="fa-solid fa-eye text-muted fs-5" title="Always visible"></i>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

const LOCKED_SECTION_TYPES = ['ScheduleTable', 'ExecutionPage'];

export default {
    name: 'ContractSequence',
    components: { draggable },
    props: {
        modelValue: { type: Array, default: () => [] }
    },
    emits: ['update:modelValue', 'save'],
    data() {
        return {
            isSaving: false,
            draggableSections: [],
            lockedSections: []
        };
    },
    watch: {
        modelValue: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    const all = JSON.parse(JSON.stringify(newVal));
                    this.draggableSections = all.filter(s => !LOCKED_SECTION_TYPES.includes(s.type));
                    // Preserve the fixed order: ScheduleTable first, then ExecutionPage
                    this.lockedSections = LOCKED_SECTION_TYPES
                        .map(type => all.find(s => s.type === type))
                        .filter(Boolean);
                    // Ensure locked sections are never hidden
                    this.lockedSections.forEach(s => { s.is_hidden = false; });
                }
            },
            immediate: true
        }
    },
    methods: {
        formatSectionName(type) {
            const names = {
                MasterTerms: 'Master Terms',
                ServiceModuleStandard: 'Service Module — Standard',
                ServiceModuleTerm: 'Service Module — Term',
                ServiceModuleSubscription: 'Service Module — Subscription',
                ScheduleTable: 'Schedule',
                ExecutionPage: 'Execution Page',
            };
            return names[type] || type;
        },

        toggleDraggableVisibility(index) {
            this.draggableSections[index].is_hidden = !this.draggableSections[index].is_hidden;
        },

        onReorderEnd() {
            // Reassign sequence numbers: draggable first, locked always last
            const combined = [...this.draggableSections, ...this.lockedSections];
            combined.forEach((s, i) => { s.sequence = i + 1; });
        },

        async saveSequence() {
            this.isSaving = true;
            try {
                this.onReorderEnd();
                // Force locked sections to always be visible
                this.lockedSections.forEach(s => { s.is_hidden = false; });
                const combined = [...this.draggableSections, ...this.lockedSections];
                this.$emit('update:modelValue', JSON.parse(JSON.stringify(combined)));
                this.$emit('save');
            } catch (e) {
                this.$toast.error('Failed to save sequence.');
            } finally {
                this.isSaving = false;
            }
        }
    }
}
</script>

<style scoped>
.draggable-row {
    cursor: grab;
}
.draggable-row:active {
    cursor: grabbing !important;
}
.transition-all {
    transition: all 0.2s ease;
}
.text-vertex-blue {
    color: #00A0C1;
}
</style>
